import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TableData } from "@/App/InvestigationSales/Table";
import {
  getInvestigationSales,
  InvestigationSalesFilters,
  InvestigationSalesGrouped,
} from "@/Api/sales";
import { getSelectProductGroups } from "@/Api/productGroups";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { SelectComponent } from "@/components/SelectComponent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";

const CONTEST_STATUS = {
  CREATED: 1,
  EXPIRED: 2,
  FINISHED: 3,
  CANCELED: 4,
} as const;

export interface InvestigationSaleProps {
  id: number;
  uuid: string;
  sale_id: number;
  contest_product_id: number;
  product_classification_id: number;
  investigation_price_per_share: string | number;
  investigation_price_total: string | number;
  receive_by_cashier: boolean;
  non_winning_ticket?: boolean;
  author_id: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  sale?: any;
  author: any | null;
  contest_product_classification?: {
    id: number;
    contest_id: number;
    lottery_id: number;
    product_name: string;
    product_classification_id: number;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    contest: {
      id: number;
      name: string;
    };
    lottery: {
      id: number;
      name: string;
      color: string;
    };
  };
  product_classification?: {
    id: number;
    uuid: string;
    lottery_id: number;
    name: string;
    game_quantity: number;
    dez_quantity: string;
    quota_quantity: number;
    total_without_tariff: number;
    total_with_tariff: number;
    game_percentage: number;
    tariff: number;
    sale_price: number;
    price_without_tariff: number;
    commission_by_quote: number;
    image: string | null;
    is_active: number;
    product_type_id: number;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
}

const ACTIVE_FILTER = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;

type InvestigationFilters = {
  contest_name: string;
  lottery_id: string;
  situation: (typeof ACTIVE_FILTER)[keyof typeof ACTIVE_FILTER];
  start_date: string;
  end_date: string;
};

/** Primeiro e último dia do mês atual (yyyy-mm-dd, fuso local) */
const getCurrentMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start_date: first.toLocaleDateString("sv-SE"),
    end_date: last.toLocaleDateString("sv-SE"),
  };
};

// Padrão: concursos ativos com validade no mês atual
const getDefaultFilters = (): InvestigationFilters => ({
  contest_name: "",
  lottery_id: "",
  situation: ACTIVE_FILTER.ACTIVE,
  ...getCurrentMonthRange(),
});

export const PageList = () => {
  const [listData, setListData] = useState<ApiDataProps<InvestigationSalesGrouped>>({
    current_page: 1,
    last_page: 1,
    next_page_url: null,
    data: [],
  });
  const [state, setState] = useState("loading");
  const [selectedStatus, setSelectedStatus] = useState<number>(CONTEST_STATUS.CREATED);
  const [filters, setFilters] = useState<InvestigationFilters>(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<InvestigationFilters>(filters);
  const [showFilters, setShowFilters] = useState(false);
  const [lotteries, setLotteries] = useState<Array<{ id: number; name: string }>>([]);

  const { hasPermission } = usePermissions();

  const buildParams = (
    currentFilters: InvestigationFilters,
    status: number
  ): InvestigationSalesFilters => ({
    contest_status_id: status,
    contest_name: currentFilters.contest_name.trim() || undefined,
    lottery_id: currentFilters.lottery_id ? Number(currentFilters.lottery_id) : undefined,
    is_active: currentFilters.situation === ACTIVE_FILTER.ACTIVE,
    start_date: currentFilters.start_date || undefined,
    end_date: currentFilters.end_date || undefined,
  });

  const fetchPage = async (
    page: number,
    currentFilters: InvestigationFilters = appliedFilters,
    status: number = selectedStatus
  ) => {
    const { data } = await getInvestigationSales(page, buildParams(currentFilters, status));
    const paginatedData = data?.response;
    setListData({
      current_page: paginatedData?.current_page || page,
      last_page: paginatedData?.last_page || 1,
      next_page_url: paginatedData?.next_page_url || null,
      data: paginatedData?.data || [],
    });
  };

  const loadPage = async (
    page: number,
    currentFilters: InvestigationFilters = appliedFilters,
    status: number = selectedStatus
  ) => {
    try {
      setState("loading");
      await fetchPage(page, currentFilters, status);
    } catch (error) {
      console.error(error);
    } finally {
      setState("production");
    }
  };

  useEffect(() => {
    loadPage(1, appliedFilters, selectedStatus);
  }, [selectedStatus]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getSelectProductGroups();
        setLotteries(data?.response || []);
      } catch (error) {
        console.error("Erro ao buscar loterias:", error);
      }
    })();
  }, []);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    loadPage(1, filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    loadPage(1, defaultFilters);
  };

  const defaultFilters = getDefaultFilters();
  const hasActiveFilters =
    filters.contest_name !== "" ||
    filters.lottery_id !== "" ||
    filters.situation !== defaultFilters.situation ||
    filters.start_date !== defaultFilters.start_date ||
    filters.end_date !== defaultFilters.end_date;

  const pageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await loadPage(listData?.current_page + 1);
      }
    },
    refresh: async () => {
      await loadPage(listData?.current_page || 1);
    },
    silentRefresh: async () => {
      await fetchPage(listData?.current_page || 1);
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await loadPage(listData?.current_page - 1);
      }
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await loadPage(listData?.last_page);
      }
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-sales-calculation-list")}>
        <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
          <h2 className="text-2xl font-semibold flex gap-2 items-center">
            Apuração de Vendas
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Status do Concurso:</span>
            <SelectComponent
              label=""
              options={[
                { value: String(CONTEST_STATUS.CREATED), label: "Criado" },
                { value: String(CONTEST_STATUS.EXPIRED), label: "Expirado" },
                { value: String(CONTEST_STATUS.FINISHED), label: "Finalizado" },
                { value: String(CONTEST_STATUS.CANCELED), label: "Cancelado" },
              ]}
              onChange={(value) => setSelectedStatus(Number(value))}
              placeholder="Selecione o status"
              value={String(selectedStatus)}
            />
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Filtros</CardTitle>
              <div className="flex gap-2">
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearFilters}
                    className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 bg-red-50"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpar
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className={cn(
                    "h-8",
                    showFilters && cn(colors.bg_primary, "text-white hover:text-white")
                  )}
                >
                  {showFilters ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Ocultar
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Expandir
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>

          {showFilters && (
            <CardContent className="space-y-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="filter-contest-name">Concurso</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="filter-contest-name"
                      placeholder="Número ou nome"
                      value={filters.contest_name}
                      onChange={(e) =>
                        setFilters({ ...filters, contest_name: e.target.value })
                      }
                      onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                      className="pl-9"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-lottery">Loteria</Label>
                  <Select
                    value={filters.lottery_id || "all"}
                    onValueChange={(value) =>
                      setFilters({ ...filters, lottery_id: value === "all" ? "" : value })
                    }
                  >
                    <SelectTrigger id="filter-lottery">
                      <SelectValue placeholder="Todas as loterias" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {lotteries.map((lottery) => (
                        <SelectItem key={lottery.id} value={String(lottery.id)}>
                          {lottery.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-situation">Situação do concurso</Label>
                  <Select
                    value={filters.situation}
                    onValueChange={(value) =>
                      setFilters({
                        ...filters,
                        situation: value as InvestigationFilters["situation"],
                      })
                    }
                  >
                    <SelectTrigger id="filter-situation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ACTIVE_FILTER.ACTIVE}>Ativos</SelectItem>
                      <SelectItem value={ACTIVE_FILTER.INACTIVE}>Inativos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-start-date">Validade (início)</Label>
                  <Input
                    id="filter-start-date"
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-end-date">Validade (fim)</Label>
                  <Input
                    id="filter-end-date"
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleApplyFilters}
                  className={cn("px-6", colors.bg_primary)}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Aplicar Filtros
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        <div className="flex flex-col gap-3">
          {state === "loading" ? (
            <CreateLoaderTable cols={5} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              selectedStatus={selectedStatus}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
